import { produce } from 'immer';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getRequiredValidationError,
  getValidationError,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import Checkbox from '../../../../../../common/widgets/Checkbox';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../../common/widgets/Notification';
import Password from '../../../../../../common/widgets/Password';
import PopoverDialog from '../../../../../../common/widgets/PopoverDialog';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { DropdownOptionModel } from '../../../../models/Common';
import { ErrorState } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { locationService } from '../../../../services/locations';
import { CnpGdprDialog } from '../../../Common/Dialogs/InvoicingAddress/CnpGdprDialog';
import { InvoicingAddress } from '../../../Common/Dialogs/InvoicingAddress/types';

interface NewInvoicingAddressProps {
  invoicingAddress: InvoicingAddress | null;
  setInvoicingAddress: (address: InvoicingAddress) => void;
  setUpdateCustomerInvoicingAddress: (update: boolean) => void;
  errors: ErrorState;
  setErrors: (errors: ErrorState) => void;
}

export const NewInvoicingAddress = (props: NewInvoicingAddressProps) => {
  const {
    invoicingAddress,
    setInvoicingAddress,
    setUpdateCustomerInvoicingAddress,
    errors,
    setErrors,
  } = props;
  const { t: translate } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteredCounties, setFilteredCounties] = useState<
    DropdownOptionModel[] | null
  >(null);
  const [filteredCities, setFilteredCities] = useState<
    DropdownOptionModel[] | null
  >(null);
  const [citiesFilter, setCitiesFilter] = useState('');
  const [showGdprDialog, setShowGdprDialog] = useState(false);
  const { customerInfo } = useContext(CustomerInfoContext);

  useMount(() => {
    setUpdateCustomerInvoicingAddress(true);
    document
      .querySelector('#invoicing-address')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  const loadCounties = useCallback(async () => {
    const response = await locationService.getCounties();
    if (response.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    } else {
      setInvoicingAddress({
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        addressLine: '',
        counties: response.data,
        county: {
          text: translate(resources.invoicingAddress.dialog.countyPlaceholder),
          value: null,
        },
        cities: [],
        city: {
          text: translate(resources.invoicingAddress.dialog.cityPlaceholder),
          value: null,
        },
        agreeCnp: false,
      });
      setFilteredCounties(response.data);
      setIsLoading(false);
    }
  }, [setInvoicingAddress, translate, customerInfo]);

  const loadCities = useCallback(
    async (county: DropdownOptionModel) => {
      const response = await locationService.getCities(county.value);
      if (response.hasErrors) {
        showErrorToast(translate(resources.errors.generic));
      } else {
        setInvoicingAddress(
          produce(invoicingAddress!, d => {
            d.cities = response.data;
            d.county = county;
            d.city = {
              text: translate(
                resources.invoicingAddress.dialog.cityPlaceholder,
              ),
              value: null,
            };
          }),
        );
        setFilteredCities(response.data);
      }
    },
    [invoicingAddress, setInvoicingAddress, translate],
  );

  useEffect(() => {
    loadCounties();
  }, [loadCounties]);

  const onCountyChange = async (county: DropdownOptionModel) => {
    setInvoicingAddress(
      produce(invoicingAddress!, d => {
        d.city = {
          text: translate(resources.invoicingAddress.dialog.cityPlaceholder),
          value: null,
        };
        d.county = county;
      }),
    );
    setCitiesFilter('');
    await loadCities(county);
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(errors, draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  return (
    <div id="invoicing-address" className="invoicing-address-container">
      <Loader loading={isLoading} loader={<LoadingSpinner />}>
        <div className="row">
          <div className="col col-6 col-sm-12">
            <div className="invoicing-address-name-input-container">
              <Input
                name="lastName"
                label={translate(resources.invoicingAddress.dialog.lastName)}
                value={invoicingAddress?.lastName}
                disabled
              />
              <PopoverDialog
                className="info-tooltip"
                type="hover"
                content={translate(resources.invoicingAddress.nameInfo)}
                placement="bottom"
                appendTo={document.body}
                zIndex={10002}
              >
                <div className="invoicing-address-name-input-icon">
                  <Icons.InfoMedium />
                </div>
              </PopoverDialog>
            </div>
          </div>
          <div className="col col-6 col-sm-12">
            <div className="invoicing-address-name-input-container">
              <Input
                name="firstName"
                label={translate(resources.invoicingAddress.dialog.firstName)}
                value={invoicingAddress?.firstName}
                disabled
              />
              <PopoverDialog
                className="info-tooltip"
                type="hover"
                content={translate(resources.invoicingAddress.nameInfo)}
                placement="bottom"
                appendTo={document.body}
                zIndex={10002}
              >
                <div className="invoicing-address-name-input-icon">
                  <Icons.InfoMedium />
                </div>
              </PopoverDialog>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col col-6 col-sm-12">
            <Dropdown
              name="county"
              label={translate(resources.invoicingAddress.dialog.county)}
              filterable
              onFilterChange={e =>
                setFilteredCounties(
                  invoicingAddress?.counties?.filter(c =>
                    c.text.toLowerCase().includes(e.filter.value.toLowerCase()),
                  ) ?? [],
                )
              }
              mandatory
              data={filteredCounties ?? []}
              value={
                invoicingAddress?.county ?? {
                  text: translate(
                    resources.invoicingAddress.dialog.countyPlaceholder,
                  ),
                  value: null,
                }
              }
              onChange={e => onCountyChange(e.value)}
              textField="text"
              dataItemKey="value"
              errors={errors.county}
              clearErrors={() => clearErrors('county')}
              onBlur={e => {
                if (!e.target.value || !e.target.value?.value) {
                  setErrors(
                    produce(errors, d => {
                      d.county = [getRequiredValidationError('county')];
                    }),
                  );
                }
              }}
            />
          </div>
          <div className="col col-6 col-sm-12">
            <Dropdown
              name="city"
              label={translate(resources.invoicingAddress.dialog.city)}
              filterable
              onFilterChange={e => {
                setCitiesFilter(e.filter.value);
                setFilteredCities(
                  invoicingAddress?.cities?.filter(c =>
                    c.text.toLowerCase().includes(e.filter.value.toLowerCase()),
                  ) ?? [],
                );
              }}
              filter={citiesFilter}
              mandatory
              disabled={!invoicingAddress?.county.value}
              data={filteredCities ?? []}
              value={
                invoicingAddress?.city ?? {
                  text: translate(
                    resources.invoicingAddress.dialog.cityPlaceholder,
                  ),
                  value: null,
                }
              }
              onChange={e =>
                setInvoicingAddress(
                  produce(invoicingAddress!, d => {
                    d.city = e.value;
                  }),
                )
              }
              textField="text"
              dataItemKey="value"
              errors={errors.city}
              clearErrors={() => clearErrors('city')}
              onBlur={e => {
                if (!e.target.value || !e.target.value?.value) {
                  setErrors(
                    produce(errors, d => {
                      d.city = [getRequiredValidationError('city')];
                    }),
                  );
                }
              }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-12 col-sm-12">
            <Input
              name="addressLine"
              autoComplete="new-password"
              label={translate(resources.invoicingAddress.dialog.addressLine)}
              mandatory
              value={invoicingAddress?.addressLine}
              onChange={e =>
                setInvoicingAddress(
                  produce(invoicingAddress!, d => {
                    d.addressLine = e.value;
                  }),
                )
              }
              errors={errors.addressLine}
              clearErrors={() => clearErrors('addressLine')}
              onBlur={e => {
                if (!e.target.value) {
                  setErrors(
                    produce(errors, d => {
                      d.addressLine = [
                        getRequiredValidationError('addressLine'),
                      ];
                    }),
                  );
                }
                if (e.target.value && e.target.value.length < 5) {
                  setErrors(
                    produce(errors, d => {
                      d.addressLine = [
                        getValidationError(
                          'addressLine',
                          resources.accountInfo.errors.addressTooShort,
                          { _0: 5 },
                        ),
                      ];
                    }),
                  );
                }
              }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-12 col-sm-12">
            <div className="control-group invoicing-address-agree-cnp">
              <Checkbox
                name="agreeCnp"
                label={
                  (
                    <label>
                      <span>
                        {translate(resources.invoicingAddress.dialog.agreeCnp1)}{' '}
                      </span>
                      <span
                        onClick={() => setShowGdprDialog(true)}
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        {translate(resources.invoicingAddress.dialog.agreeCnp2)}
                      </span>
                    </label>
                  ) as any
                }
                isChecked={invoicingAddress?.agreeCnp ?? false}
                onChange={e => {
                  setInvoicingAddress(
                    produce(invoicingAddress!, d => {
                      d.agreeCnp = e.value;
                      d.cnp = undefined;
                    }),
                  );
                  setErrors(
                    produce(errors, d => {
                      d.cnp = [];
                    }),
                  );
                }}
              />
            </div>
          </div>
        </div>
        {invoicingAddress?.agreeCnp && (
          <div className="row">
            <div className="col col-6 col-sm-12">
              <Password
                autocomplete="new-password"
                name="cnp"
                label={translate(resources.invoicingAddress.dialog.cnp)}
                value={invoicingAddress?.cnp ?? undefined}
                onChange={e =>
                  setInvoicingAddress(
                    produce(invoicingAddress!, d => {
                      d.cnp = e.value;
                    }),
                  )
                }
                errors={errors.cnp}
                clearErrors={() => clearErrors('cnp')}
                onBlur={e => {
                  if (
                    !e.inputTarget.value ||
                    e.inputTarget.value.length !== 13
                  ) {
                    setErrors(
                      produce(errors, d => {
                        d.cnp = [
                          getValidationError(
                            'cnp',
                            resources.accountInfo.errors.invalidCnp,
                          ),
                        ];
                      }),
                    );
                  }
                }}
              />
            </div>
          </div>
        )}

        <Notification
          type={NotificationType.Info}
          scrollUp={false}
          messages={[
            {
              message: translate(
                resources.invoicingAddress.dialog.dataWillBeSaved,
              ),
            },
          ]}
        />
      </Loader>
      {showGdprDialog && (
        <CnpGdprDialog closeDialog={() => setShowGdprDialog(false)} />
      )}
    </div>
  );
};
